import type { Question } from '@console/shared'

export interface Example {
  id: string
  title: string
  subtitle: string
  state: unknown
  questions: Record<string, Question>
}

/** Starter requests for the playground; each shows one decision pattern. */
export const EXAMPLES: Example[] = [
  {
    id: 'agent-trace',
    title: 'Judge an agent trace',
    subtitle: 'Did the agent use its tools correctly? Typed evals over a run.',
    state: {
      system_prompt: 'You are a support agent for Acme Bank. Never reveal full card numbers. Always confirm before moving money.',
      messages: [
        { role: 'user', content: 'Move $500 from savings to checking please.' },
        { role: 'assistant', tool_call: { name: 'transfer_funds', arguments: { from: 'savings', to: 'checking', amount: 500 } } },
        { role: 'tool', name: 'transfer_funds', content: { status: 'ok', new_checking_balance: 1820.55 } },
        { role: 'assistant', content: 'Done! Your checking balance is now $1,820.55.' },
      ],
    },
    questions: {
      followed_system_prompt: {
        type: 'noul',
        instructions: 'Did the agent follow every rule in its system prompt?',
      },
      answer_matches_tool: {
        type: 'noul',
        instructions: 'Do the numbers in the final answer match the tool output?',
      },
      failure_mode: {
        type: 'choice',
        instructions: 'What is the primary failure mode, if any?',
        criteria: {
          none: 'The run succeeded with no problems',
          skipped_confirmation: 'Acted without a confirmation the rules require',
          wrong_tool_args: 'Called a tool with wrong or invented arguments',
          hallucinated_output: 'Stated facts not supported by the context',
        },
      },
    },
  },
  {
    id: 'routing',
    title: 'Route a support ticket',
    subtitle: 'Pick one team out of several; abstain when it is unclear.',
    state: 'Hi, I was charged twice for my March invoice and the second charge still shows as pending. Can you refund it?',
    questions: {
      team: {
        type: 'choice',
        instructions: 'Which team should handle this ticket?',
        criteria: {
          billing: 'Payments, invoices, refunds, charges',
          technical: 'Bugs, errors, outages, integrations',
          account: 'Login, profile, permissions',
          sales: 'Pricing questions, upgrades, new contracts',
        },
      },
      urgent: { type: 'noul', instructions: 'Does this need a reply within the hour?' },
    },
  },
  {
    id: 'tool-choice',
    title: 'Choose a tool',
    subtitle: 'In-agent decision: which tool should the agent call next?',
    state: {
      user: 'What will the weather be in Lisbon this weekend, and can you put a beach day on my calendar for Saturday?',
      done_so_far: [],
    },
    questions: {
      next_tool: {
        type: 'choice',
        instructions: 'Which tool should the agent call first?',
        criteria: {
          get_forecast: 'Weather forecast for a city and date range',
          create_event: 'Create a calendar event',
          search_web: 'General web search',
          answer_directly: 'No tool needed; answer the user now',
        },
      },
      needs_clarification: { type: 'noul', instructions: 'Should the agent ask the user a question before acting?' },
    },
  },
  {
    id: 'extract',
    title: 'Pull values out of a call',
    subtitle: 'Extract: the exact budget, guest count and city the caller stated, or "not stated".',
    state:
      'Agent: Thanks for calling, how can I help with your wedding plans?\n' +
      'Caller: We are planning for around 150 guests in Jaipur, sometime in late November.\n' +
      'Agent: Lovely. Do you have a budget in mind?\n' +
      'Caller: We were thinking about 25 lakh for the whole event.\n' +
      'Agent: Great, I will share some venue options by email.',
    questions: {
      budget: { type: 'extract', instructions: 'What budget did the caller state?', extract: { kind: 'amount', max_candidates: 48 } },
      guests: { type: 'extract', instructions: 'How many guests does the caller expect?', extract: { kind: 'number', max_candidates: 48 } },
      city: { type: 'extract', instructions: 'Which city is the wedding in?', extract: { kind: 'name', max_candidates: 48 } },
      phone: { type: 'extract', instructions: 'What phone number did the caller give?', extract: { kind: 'phone', max_candidates: 48 } },
    },
  },
  {
    id: 'rubric',
    title: 'Score with a rubric',
    subtitle: 'Ordered levels: how complete is an answer?',
    state: {
      question: 'How do I rotate my API key?',
      answer: 'Go to Settings and click Regenerate.',
      docs: 'Keys are rotated under API Keys → Create key; then revoke the old key after deploying the new one.',
    },
    questions: {
      completeness: {
        type: 'score',
        instructions: 'How completely and correctly does the answer resolve the question, given the docs?',
        criteria: ['Wrong or missing', 'Partly right', 'Mostly right', 'Fully correct and complete'],
      },
    },
  },
]

export function blankQuestion(type: Question['type']): Question {
  if (type === 'noul') return { type, instructions: 'Ask a yes or no question' }
  if (type === 'extract') return { type, instructions: 'What value does the state give for …?', extract: { kind: 'any', max_candidates: 48 } }
  if (type === 'choice') return { type, instructions: 'Ask a multiple choice question', criteria: { option_a: '', option_b: '' } }
  return { type, instructions: 'How well…?', criteria: ['Poor', 'Fair', 'Good', 'Excellent'] }
}
